import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang, LANGS } from "@/i18n";
import { CONTACT } from "@/data/site";
import { Phone, Menu, X, FlaskConical, Instagram } from "lucide-react";

const links = [
  ["nav_services", "#services"], ["nav_wellness", "#wellness"], ["nav_home_collection", "#home-collection"],
  ["nav_hospitals", "#hospitals"], ["nav_careers", "#careers"], ["nav_about", "#about"], ["nav_contact", "#contact"],
];

export default function Navbar({ onBook }) {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav border-b border-slate-100 shadow-sm" : "bg-transparent"}`} data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5" data-testid="navbar-logo">
          <div className="w-10 h-10 rounded-2xl bg-navy flex items-center justify-center">
            <FlaskConical className="w-5 h-5 text-turq-light" strokeWidth={2} />
          </div>
          <div className="leading-tight">
            <span className="block font-heading font-bold text-navy text-lg">DOABA</span>
            <span className="block text-[10px] tracking-[0.2em] uppercase text-turq-dark font-semibold">Diagnostics</span>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-5">
          {links.map(([key, href]) => (
            <a key={key} href={href} className="text-sm font-medium text-navy/80 hover:text-turq-dark transition-colors" data-testid={`nav-${key}`}>
              {t(key)}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a href={CONTACT.instagram} target="_blank" rel="noreferrer" aria-label="Instagram"
            className="hidden md:flex w-9 h-9 rounded-full bg-slate-100 hover:bg-turq hover:text-white text-navy items-center justify-center transition-colors" data-testid="navbar-instagram">
            <Instagram className="w-4 h-4" />
          </a>
          <div className="hidden sm:flex items-center bg-slate-100 rounded-full p-1" data-testid="lang-toggle">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all ${lang === l.code ? "bg-navy text-white" : "text-navy/60"}`}
                data-testid={`lang-${l.code}`}>{l.label}</button>
            ))}
          </div>
          <button onClick={onBook} className="hidden md:inline-flex items-center gap-2 bg-turq hover:bg-turq-dark text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors shadow-[0_8px_24px_rgba(0,180,216,0.3)]" data-testid="navbar-book-btn">
            {t("book_appointment")}
          </button>
          <a href={`tel:${CONTACT.phoneRaw}`} className="md:hidden w-10 h-10 rounded-full bg-turq flex items-center justify-center text-white" data-testid="navbar-call-mobile"><Phone className="w-4 h-4" /></a>
          <button className="lg:hidden w-10 h-10 flex items-center justify-center text-navy" onClick={() => setOpen(!open)} data-testid="navbar-menu-btn">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass-nav border-t border-slate-100 px-5 py-4 space-y-1" data-testid="mobile-menu">
          {links.map(([key, href]) => (
            <a key={key} href={href} onClick={() => setOpen(false)} className="block py-2.5 text-navy font-medium" data-testid={`mobile-nav-${key}`}>{t(key)}</a>
          ))}
          <div className="flex items-center gap-2 pt-2">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${lang === l.code ? "bg-navy text-white" : "bg-slate-100 text-navy/60"}`}>{l.label}</button>
            ))}
          </div>
          <button onClick={() => { setOpen(false); onBook(); }} className="w-full mt-2 bg-turq text-white rounded-full py-3 font-medium" data-testid="mobile-book-btn">{t("book_appointment")}</button>
        </div>
      )}
    </header>
  );
}
